/**
 * Connector Schemas
 *
 * Zod validation schemas for connector operations.
 * Issue #244: Added for SFR Analytics integration.
 *
 * These schemas ensure type-safe validation of connector configurations,
 * credentials, and API responses.
 */

import { z } from 'zod';

// ============================================================================
// CONNECTOR TYPES
// ============================================================================

/**
 * Connector status enum
 */
export const ConnectorStatusSchema = z.enum(['connected', 'disconnected', 'error', 'pending']);
export type ConnectorStatus = z.infer<typeof ConnectorStatusSchema>;

/**
 * Connector type enum
 * Issue #300: Added retell, resend, docuseal, openai
 */
export const ConnectorTypeSchema = z.enum([
  'salesforce',
  'slack',
  'sfr-analytics',
  'smarty-streets',
  'google',
  'github',
  'retell',
  'resend',
  'docuseal',
  'openai',
  'custom',
]);
export type ConnectorType = z.infer<typeof ConnectorTypeSchema>;

// ============================================================================
// CONNECTOR SCHEMAS
// ============================================================================

/**
 * Connector configuration schema
 * Generic configuration that varies by connector type
 */
export const ConnectorConfigSchema = z.record(z.string(), z.unknown());
export type ConnectorConfig = z.infer<typeof ConnectorConfigSchema>;

/**
 * Connector DTO schema
 */
export const ConnectorDtoSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().min(1),
  name: z.string().min(1).max(255),
  type: z.string().min(1),
  status: ConnectorStatusSchema,
  config: ConnectorConfigSchema,
  lastHealthCheck: z.string().datetime().optional(),
  error: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ConnectorDto = z.infer<typeof ConnectorDtoSchema>;

/**
 * Create connector request schema
 */
export const CreateConnectorRequestSchema = z.object({
  type: z.string().min(1),
  name: z.string().min(1).max(255),
  config: ConnectorConfigSchema,
});
export type CreateConnectorRequest = z.infer<typeof CreateConnectorRequestSchema>;

/**
 * Update connector request schema
 */
export const UpdateConnectorRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  config: ConnectorConfigSchema.optional(),
  status: z.enum(['connected', 'disconnected']).optional(),
});
export type UpdateConnectorRequest = z.infer<typeof UpdateConnectorRequestSchema>;

/**
 * Connector health check response schema
 */
export const ConnectorHealthSchema = z.object({
  connectorId: z.string().uuid(),
  healthy: z.boolean(),
  latencyMs: z.number().int().nonnegative(),
  lastCheck: z.string().datetime(),
  error: z.string().optional(),
});
export type ConnectorHealth = z.infer<typeof ConnectorHealthSchema>;

/**
 * Connector test response schema
 */
export const ConnectorTestResponseSchema = z.object({
  success: z.boolean(),
  latencyMs: z.number().int().nonnegative(),
  message: z.string().optional(),
});
export type ConnectorTestResponse = z.infer<typeof ConnectorTestResponseSchema>;

// ============================================================================
// CREDENTIAL SCHEMAS
// ============================================================================

/**
 * Connector credential schema (decrypted)
 * Used internally after decryption
 */
export const ConnectorCredentialSchema = z.object({
  keyName: z.string().min(1),
  value: z.string().min(1),
});
export type ConnectorCredential = z.infer<typeof ConnectorCredentialSchema>;

/**
 * Credential key names that should be encrypted
 */
export const EncryptedCredentialKeys = [
  'apiKey',
  'api_key',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'clientSecret',
  'client_secret',
  'password',
  'privateKey',
  'private_key',
] as const;

export type EncryptedCredentialKey = (typeof EncryptedCredentialKeys)[number];

/**
 * Check if a key should be encrypted
 */
export function isEncryptedCredentialKey(key: string): key is EncryptedCredentialKey {
  return EncryptedCredentialKeys.includes(key as EncryptedCredentialKey);
}
